/* ============================================================
   HEAT WAVE - EASYFLOW CUSTOM SELECT + INVENTORY
   ============================================================ */

(() => {
  const ROOT_SELECTOR =
    '.ef__product-option-root, .ef__product-option';

  const SELECT_SELECTOR =
    '.ef__option-values select';

  const productCache =
    new Map();


  /* ==========================================================
     EASYFLOW-ONLY PRICE SIZE
     ----------------------------------------------------------
     Slightly increases the displayed product price ONLY when
     EasyFlow controls exist on the product page.
     ========================================================== */

  const easyFlowStyle =
    document.createElement('style');

  easyFlowStyle.textContent = `
    body:has(.ef__product-option-root) product-info price-list,
    body:has(.ef__product-option-root) .product-info__price,
    body:has(.ef__product-option) product-info price-list,
    body:has(.ef__product-option) .product-info__price {
      font-size: 1.1em !important;
    }
  `;

  document.head.appendChild(
    easyFlowStyle
  );


  /* ==========================================================
     THEME COMPATIBILITY FIX
     ========================================================== */

  function patchBuyButtons(scope = document) {
    const buyButtons = [];

    if (
      scope instanceof HTMLElement &&
      scope.matches('buy-buttons')
    ) {
      buyButtons.push(scope);
    }

    if (scope.querySelectorAll) {
      scope
        .querySelectorAll('buy-buttons')
        .forEach((element) => {
          buyButtons.push(element);
        });
    }

    buyButtons.forEach((element) => {
      if (
        element.querySelector(
          'button[type="submit"]'
        )
      ) {
        return;
      }

      if (
        element.querySelector(
          '.easyflow-theme-submit-fallback'
        )
      ) {
        return;
      }

      const fallbackButton =
        document.createElement('button');

      fallbackButton.type =
        'submit';

      fallbackButton.className =
        'easyflow-theme-submit-fallback';

      fallbackButton.style.display =
        'none';

      fallbackButton.setAttribute(
        'aria-hidden',
        'true'
      );

      fallbackButton.tabIndex =
        -1;

      if (
        !element.hasAttribute(
          'template'
        )
      ) {
        element.setAttribute(
          'template',
          'product'
        );
      }

      element.appendChild(
        fallbackButton
      );
    });
  }


  /* ==========================================================
     EASYFLOW ADD-ON INVENTORY
     ========================================================== */

  async function removeSoldOutOptions(
    select
  ) {
    if (
      !(
        select instanceof
        HTMLSelectElement
      )
    ) {
      return;
    }

    const options =
      Array.from(
        select.querySelectorAll(
          'option[data-addon-handle][data-variant-id]'
        )
      );

    for (const option of options) {
      const handle =
        option.dataset.addonHandle;

      const variantId =
        option.dataset.variantId
          ?.split('/')
          .pop();

      if (
        !handle ||
        !variantId
      ) {
        continue;
      }

      try {
        let product;

        if (
          productCache.has(
            handle
          )
        ) {
          product =
            productCache.get(
              handle
            );
        } else {
          const root =
            window.Shopify
              ?.routes
              ?.root ||
            '/';

          const response =
            await fetch(
              root +
              'products/' +
              handle +
              '.js',
              {
                cache: 'no-store'
              }
            );

          /*
           * Fail open.
           * If Shopify can't return inventory,
           * leave the EasyFlow option visible.
           */
          if (!response.ok) {
            console.warn(
              'EasyFlow inventory: could not load',
              handle
            );

            continue;
          }

          product =
            await response.json();

          productCache.set(
            handle,
            product
          );
        }

        const variant =
          product
            ?.variants
            ?.find(
              (variant) =>
                String(
                  variant.id
                ) ===
                String(
                  variantId
                )
            );

        if (!variant) {
          console.warn(
            'EasyFlow inventory: variant not found',
            variantId,
            handle
          );

          continue;
        }

        /*
         * Remove sold-out EasyFlow add-on.
         */
        if (
          variant.available ===
          false
        ) {
          console.log(
            'EasyFlow inventory: hiding unavailable option:',
            option.textContent.trim()
          );

          option.remove();
        }

      } catch (error) {
        console.error(
          'EasyFlow inventory check failed:',
          handle,
          error
        );
      }
    }
  }


  /* ==========================================================
     CUSTOM EASYFLOW DROPDOWN
     ========================================================== */

  function closeAllDropdowns(
    except = null
  ) {
    document
      .querySelectorAll(
        '.ef-custom-select.is-open'
      )
      .forEach((dropdown) => {
        if (
          dropdown === except
        ) {
          return;
        }

        dropdown.classList.remove(
          'is-open'
        );

        const button =
          dropdown.querySelector(
            '.ef-custom-select__button'
          );

        if (button) {
          button.setAttribute(
            'aria-expanded',
            'false'
          );
        }
      });
  }


  async function buildCustomSelect(
    select
  ) {
    if (
      !(
        select instanceof
        HTMLSelectElement
      )
    ) {
      return;
    }

    /*
     * Prevent duplicate async builds.
     */
    if (
      select.dataset.customized ===
        'true' ||
      select.dataset.customized ===
        'loading'
    ) {
      return;
    }

    select.dataset.customized =
      'loading';


    /*
     * Remove unavailable add-ons BEFORE
     * building the visible custom dropdown.
     */
    await removeSoldOutOptions(
      select
    );


    /*
     * EasyFlow may have rebuilt this control
     * while inventory was being checked.
     */
    if (!select.isConnected) {
      return;
    }


    select.dataset.customized =
      'true';

    select.classList.add(
      'ef-native-select'
    );


    const customSelect =
      document.createElement(
        'div'
      );

    customSelect.className =
      'ef-custom-select';


    const button =
      document.createElement(
        'button'
      );

    button.type =
      'button';

    button.className =
      'ef-custom-select__button';

    button.setAttribute(
      'aria-haspopup',
      'listbox'
    );

    button.setAttribute(
      'aria-expanded',
      'false'
    );


    const buttonText =
      document.createElement(
        'span'
      );

    buttonText.className =
      'ef-custom-select__button-text';


    const arrow =
      document.createElement(
        'span'
      );

    arrow.className =
      'ef-custom-select__arrow';

    arrow.setAttribute(
      'aria-hidden',
      'true'
    );


    button.append(
      buttonText,
      arrow
    );


    const list =
      document.createElement(
        'div'
      );

    list.className =
      'ef-custom-select__list';

    list.setAttribute(
      'role',
      'listbox'
    );

    list.tabIndex =
      -1;


    function syncFromNativeSelect() {
      const selectedOption =
        select.options[
          select.selectedIndex
        ];

      buttonText.textContent =
        selectedOption
          ?.textContent
          ?.trim() ||
        'Choose an option';


      list
        .querySelectorAll(
          '.ef-custom-select__option'
        )
        .forEach((optionButton) => {
          const isSelected =
            optionButton
              .dataset
              .value ===
            select.value;

          optionButton
            .classList
            .toggle(
              'is-selected',
              isSelected
            );

          optionButton.setAttribute(
            'aria-selected',
            isSelected
              ? 'true'
              : 'false'
          );
        });
    }


    Array
      .from(
        select.options
      )
      .forEach((option) => {
        const optionButton =
          document.createElement(
            'button'
          );

        optionButton.type =
          'button';

        optionButton.className =
          'ef-custom-select__option';

        optionButton.dataset.value =
          option.value;

        optionButton.textContent =
          option.textContent.trim();

        optionButton.setAttribute(
          'role',
          'option'
        );

        optionButton.setAttribute(
          'aria-selected',
          'false'
        );


        if (option.disabled) {
          optionButton.disabled =
            true;

          optionButton.classList.add(
            'is-disabled'
          );
        }


        optionButton.addEventListener(
          'click',
          () => {
            if (option.disabled) {
              return;
            }

            /*
             * Make theme.js safe before EasyFlow
             * fires its events.
             */
            patchBuyButtons();


            /*
             * Change EasyFlow's real select.
             */
            select.value =
              option.value;


            select.dispatchEvent(
              new Event(
                'input',
                {
                  bubbles: true
                }
              )
            );

            select.dispatchEvent(
              new Event(
                'change',
                {
                  bubbles: true
                }
              )
            );


            syncFromNativeSelect();


            customSelect
              .classList
              .remove(
                'is-open'
              );

            button.setAttribute(
              'aria-expanded',
              'false'
            );

            button.focus();
          }
        );


        list.appendChild(
          optionButton
        );
      });


    button.addEventListener(
      'click',
      () => {
        const willOpen =
          !customSelect
            .classList
            .contains(
              'is-open'
            );

        closeAllDropdowns(
          customSelect
        );

        customSelect
          .classList
          .toggle(
            'is-open',
            willOpen
          );

        button.setAttribute(
          'aria-expanded',
          willOpen
            ? 'true'
            : 'false'
        );


        if (willOpen) {
          const selectedButton =
            list.querySelector(
              '.ef-custom-select__option.is-selected'
            ) ||
            list.querySelector(
              '.ef-custom-select__option:not(:disabled)'
            );

          selectedButton?.focus();
        }
      }
    );


    list.addEventListener(
      'keydown',
      (event) => {
        const options =
          Array.from(
            list.querySelectorAll(
              '.ef-custom-select__option:not(:disabled)'
            )
          );

        if (!options.length) {
          return;
        }

        const currentIndex =
          options.indexOf(
            document.activeElement
          );


        if (
          event.key ===
          'ArrowDown'
        ) {
          event.preventDefault();

          const nextIndex =
            currentIndex < 0
              ? 0
              : (
                  currentIndex +
                  1
                ) %
                options.length;

          options[
            nextIndex
          ]?.focus();
        }


        if (
          event.key ===
          'ArrowUp'
        ) {
          event.preventDefault();

          const previousIndex =
            currentIndex <= 0
              ? options.length - 1
              : currentIndex - 1;

          options[
            previousIndex
          ]?.focus();
        }


        if (
          event.key ===
          'Escape'
        ) {
          customSelect
            .classList
            .remove(
              'is-open'
            );

          button.setAttribute(
            'aria-expanded',
            'false'
          );

          button.focus();
        }
      }
    );


    select.addEventListener(
      'change',
      syncFromNativeSelect
    );


    customSelect.append(
      button,
      list
    );


    select.insertAdjacentElement(
      'afterend',
      customSelect
    );


    syncFromNativeSelect();
  }


  /* ==========================================================
     INITIALIZE EASYFLOW SELECTS
     ========================================================== */

  function initializeEasyFlowSelects(
    scope = document
  ) {
    if (
      scope instanceof
        HTMLSelectElement &&
      scope.matches(
        SELECT_SELECTOR
      )
    ) {
      buildCustomSelect(
        scope
      );

      return;
    }


    if (scope.querySelectorAll) {
      scope
        .querySelectorAll(
          SELECT_SELECTOR
        )
        .forEach((select) => {
          buildCustomSelect(
            select
          );
        });
    }
  }


  function initialize() {
    patchBuyButtons();

    initializeEasyFlowSelects();
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }


  const observer =
    new MutationObserver(
      (mutations) => {
        mutations.forEach(
          (mutation) => {
            mutation
              .addedNodes
              .forEach((node) => {
                if (
                  !(
                    node instanceof
                    HTMLElement
                  )
                ) {
                  return;
                }

                patchBuyButtons(
                  node
                );

                initializeEasyFlowSelects(
                  node
                );
              });
          }
        );
      }
    );


  observer.observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );


  document.addEventListener(
    'click',
    (event) => {
      if (
        !event.target.closest(
          '.ef-custom-select'
        )
      ) {
        closeAllDropdowns();
      }
    }
  );


  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key ===
        'Escape'
      ) {
        closeAllDropdowns();
      }
    }
  );


  console.log(
    'HWV EasyFlow custom select + inventory loaded.'
  );
})();



/* ============================================================
   HEAT WAVE - EASYFLOW PRODUCT VARIANT PRICE SYNC
   ------------------------------------------------------------
   ONLY runs on pages containing EasyFlow.

   Fixes Polarized / Non-Polarized Shopify variant pricing
   without hardcoded prices.

   Products without EasyFlow are untouched.
   ============================================================ */

(() => {
  let productJSON =
    null;

  let priceSyncTimer =
    null;


  function hasEasyFlow() {
    return !!document.querySelector(
      '.ef__product-option-root, .ef__product-option'
    );
  }


  /* ==========================================================
     PRODUCT JSON
     ========================================================== */

  async function getProductJSON() {
    if (productJSON) {
      return productJSON;
    }

    try {
      const response =
        await fetch(
          window.location.pathname +
          '.js',
          {
            cache: 'no-store'
          }
        );


      if (!response.ok) {
        throw new Error(
          'Could not load current Shopify product.'
        );
      }


      productJSON =
        await response.json();


      return productJSON;

    } catch (error) {
      console.error(
        'HWV EasyFlow price sync:',
        error
      );

      return null;
    }
  }


  /* ==========================================================
     MONEY
     ========================================================== */

  function formatMoney(cents) {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency: 'USD'
      }
    ).format(
      cents / 100
    );
  }


  /* ==========================================================
     FIND SHOPIFY VARIANT ID
     ========================================================== */

  function getVariantIdFromProductForm() {
    const forms =
      Array.from(
        document.querySelectorAll(
          'form[action*="/cart/add"]'
        )
      );


    for (const form of forms) {
      /*
       * Ignore EasyFlow's own forms.
       */
      if (
        form.closest(
          '.ef__product-option-root, .ef__product-option'
        )
      ) {
        continue;
      }


      const input =
        form.querySelector(
          'input[name="id"], select[name="id"]'
        );


      if (
        input &&
        /^\d+$/.test(
          String(
            input.value
          )
        )
      ) {
        return String(
          input.value
        );
      }
    }


    return null;
  }


  function getVariantIdFromURL() {
    const params =
      new URLSearchParams(
        window.location.search
      );


    const id =
      params.get(
        'variant'
      );


    if (
      id &&
      /^\d+$/.test(id)
    ) {
      return id;
    }


    return null;
  }


  /* ==========================================================
     READ SELECTED SHOPIFY OPTION VALUES
     ========================================================== */

  function getSelectedShopifyOptions() {
    const values = [];


    document
      .querySelectorAll(
        'variant-picker input:checked, ' +
        'product-variants input:checked, ' +
        '.variant-picker input:checked, ' +
        '[data-option-position] input:checked'
      )
      .forEach((input) => {
        if (
          input.closest(
            '.ef__product-option-root, .ef__product-option'
          )
        ) {
          return;
        }


        if (input.value) {
          values.push(
            String(
              input.value
            )
              .trim()
              .toLowerCase()
          );
        }
      });


    document
      .querySelectorAll(
        'variant-picker select, ' +
        'product-variants select, ' +
        '.variant-picker select, ' +
        'select[data-option-position]'
      )
      .forEach((select) => {
        if (
          select.closest(
            '.ef__product-option-root, .ef__product-option'
          )
        ) {
          return;
        }


        if (select.value) {
          values.push(
            String(
              select.value
            )
              .trim()
              .toLowerCase()
          );
        }
      });


    return values;
  }


  /* ==========================================================
     DETERMINE CURRENT SHOPIFY VARIANT
     ========================================================== */

  async function getCurrentVariant() {
    const product =
      await getProductJSON();


    if (
      !product ||
      !Array.isArray(
        product.variants
      )
    ) {
      return null;
    }


    /*
     * First choice:
     * Shopify product form variant ID.
     */
    const formVariantId =
      getVariantIdFromProductForm();


    if (formVariantId) {
      const variant =
        product.variants.find(
          (variant) =>
            String(
              variant.id
            ) ===
            String(
              formVariantId
            )
        );


      if (variant) {
        return variant;
      }
    }


    /*
     * Second choice:
     * URL variant ID.
     */
    const urlVariantId =
      getVariantIdFromURL();


    if (urlVariantId) {
      const variant =
        product.variants.find(
          (variant) =>
            String(
              variant.id
            ) ===
            String(
              urlVariantId
            )
        );


      if (variant) {
        return variant;
      }
    }


    /*
     * Final fallback:
     * Match Shopify option values.
     */
    const selectedValues =
      getSelectedShopifyOptions();


    if (
      selectedValues.length
    ) {
      const variant =
        product.variants.find(
          (variant) => {
            const variantOptions =
              (
                variant.options ||
                []
              ).map(
                (value) =>
                  String(value)
                    .trim()
                    .toLowerCase()
              );


            return selectedValues.every(
              (selected) =>
                variantOptions.includes(
                  selected
                )
            );
          }
        );


      if (variant) {
        return variant;
      }
    }


    return null;
  }


  /* ==========================================================
     UPDATE MAIN PRODUCT PRICE
     ========================================================== */

  function updateMainPrice(
    variant
  ) {
    if (
      !variant ||
      typeof variant.price !==
        'number'
    ) {
      return;
    }


    const price =
      formatMoney(
        variant.price
      );


    /*
     * Restrict price changes to main product info.
     */
    const productInfo =
      document.querySelector(
        'product-info'
      ) ||
      document.querySelector(
        '.product-info'
      );


    if (!productInfo) {
      console.warn(
        'HWV EasyFlow price sync: product-info not found.'
      );

      return;
    }


    const priceList =
      productInfo.querySelector(
        'price-list'
      );


    /*
     * Some Heat Wave templates use
     * .product-info__price instead.
     */
    if (!priceList) {
      const fallbackPrice =
        productInfo.querySelector(
          '.product-info__price'
        );


      if (fallbackPrice) {
        fallbackPrice.textContent =
          price;
      }


      return;
    }


    const priceElement =
      priceList.querySelector(
        '.sale-price'
      ) ||
      priceList.querySelector(
        '.text-on-sale'
      ) ||
      priceList.querySelector(
        'sale-price'
      ) ||
      priceList.querySelector(
        'regular-price'
      ) ||
      priceList.querySelector(
        '.money'
      );


    if (priceElement) {
      priceElement.textContent =
        price;

      return;
    }


    /*
     * Last-resort price element search.
     * Still restricted to main price-list.
     */
    const leafPrice =
      Array.from(
        priceList.querySelectorAll(
          '*'
        )
      ).find((element) => {
        const text =
          element.textContent
            ?.trim() ||
          '';


        return (
          element.children.length ===
            0 &&
          /^\$\s?[\d,.]+$/.test(
            text
          )
        );
      });


    if (leafPrice) {
      leafPrice.textContent =
        price;
    }
  }


  /* ==========================================================
     PRICE SYNC
     ========================================================== */

  async function syncPrice() {
    if (!hasEasyFlow()) {
      return;
    }


    /*
     * Let theme.js finish updating the Shopify
     * variant before reading its ID.
     */
    await new Promise(
      (resolve) =>
        setTimeout(
          resolve,
          100
        )
    );


    const variant =
      await getCurrentVariant();


    if (!variant) {
      console.warn(
        'HWV EasyFlow price sync: selected variant could not be determined.'
      );

      return;
    }


    updateMainPrice(
      variant
    );
  }


  function schedulePriceSync(
    delay = 50
  ) {
    clearTimeout(
      priceSyncTimer
    );


    priceSyncTimer =
      setTimeout(
        syncPrice,
        delay
      );
  }


  /* ==========================================================
     WATCH SHOPIFY VARIANT CONTROLS
     ========================================================== */

  document.addEventListener(
    'change',
    (event) => {
      if (!hasEasyFlow()) {
        return;
      }


      const target =
        event.target;


      if (
        !(
          target instanceof
          HTMLInputElement
        ) &&
        !(
          target instanceof
          HTMLSelectElement
        )
      ) {
        return;
      }


      /*
       * EasyFlow add-on selections do NOT change
       * the base Shopify product price.
       */
      if (
        target.closest(
          '.ef__product-option-root, .ef__product-option'
        )
      ) {
        return;
      }


      /*
       * Actual Shopify variant control.
       */
      if (
        target.closest(
          'variant-picker'
        ) ||
        target.closest(
          'product-variants'
        ) ||
        target.closest(
          '.variant-picker'
        ) ||
        target.matches(
          '[data-option-position]'
        ) ||
        target.name?.includes(
          'option'
        )
      ) {
        schedulePriceSync(
          50
        );
      }
    },
    true
  );


  /*
   * Theme variant events.
   */
  document.addEventListener(
    'variant:change',
    () => {
      if (
        hasEasyFlow()
      ) {
        schedulePriceSync(
          25
        );
      }
    }
  );


  document.documentElement
    .addEventListener(
      'variant:change',
      () => {
        if (
          hasEasyFlow()
        ) {
          schedulePriceSync(
            25
          );
        }
      }
    );


  console.log(
    'HWV EasyFlow product price sync loaded.'
  );
})();



/* ============================================================
   HEAT WAVE - EASYFLOW SIDE SHIELD CART CLEANUP
   ============================================================ */

(() => {
  const SIDE_SHIELD_PROPERTY =
    'Add Z87+ Side Shields?';

  let cleanupRunning =
    false;

  let cleanupTimer =
    null;


  /* ==========================================================
     GET CART
     ========================================================== */

  async function getCart() {
    const response =
      await fetch(
        '/cart.js',
        {
          cache: 'no-store'
        }
      );


    if (!response.ok) {
      throw new Error(
        'Could not load Shopify cart'
      );
    }


    return response.json();
  }


  /* ==========================================================
     CHECK FOR SIDE SHIELD CHILD
     ========================================================== */

  function parentHasSideShield(
    parentItem,
    cart
  ) {
    const parentId =
      parentItem
        .properties
        ?._id;


    if (!parentId) {
      return false;
    }


    return cart.items.some(
      (item) => {
        const properties =
          item.properties ||
          {};


        return (
          properties[
            '_base_product_id'
          ] ===
            parentId &&
          properties[
            '_added_in_base_product'
          ] ===
            SIDE_SHIELD_PROPERTY
        );
      }
    );
  }


  /* ==========================================================
     REMOVE STALE PROPERTY
     ========================================================== */

  async function removeStaleProperty(
    item
  ) {
    const properties = {
      ...(
        item.properties ||
        {}
      )
    };


    delete properties[
      SIDE_SHIELD_PROPERTY
    ];


    const response =
      await fetch(
        '/cart/change.js',
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json'
          },

          body:
            JSON.stringify({
              id:
                item.key,

              quantity:
                item.quantity,

              properties:
                properties
            })
        }
      );


    if (!response.ok) {
      throw new Error(
        'Could not update parent cart line'
      );
    }


    return response.json();
  }


  /* ==========================================================
     CLEAN CART
     ========================================================== */

  async function cleanCart() {
    if (cleanupRunning) {
      return;
    }


    cleanupRunning =
      true;


    try {
      const cart =
        await getCart();

      let changed =
        false;


      for (
        const item
        of cart.items
      ) {
        const properties =
          item.properties ||
          {};


        if (
          !properties[
            SIDE_SHIELD_PROPERTY
          ]
        ) {
          continue;
        }


        if (
          parentHasSideShield(
            item,
            cart
          )
        ) {
          continue;
        }


        await removeStaleProperty(
          item
        );


        changed =
          true;
      }


      if (changed) {
        document.dispatchEvent(
          new CustomEvent(
            'cart:refresh',
            {
              bubbles: true
            }
          )
        );
      }

    } catch (error) {
      console.error(
        'HWV EasyFlow cleanup:',
        error
      );

    } finally {
      cleanupRunning =
        false;


      setTimeout(
        hideSideShieldProperty,
        50
      );
    }
  }


  /* ==========================================================
     SCHEDULE CLEANUP
     ========================================================== */

  function scheduleCleanup(
    delay = 250
  ) {
    clearTimeout(
      cleanupTimer
    );


    cleanupTimer =
      setTimeout(
        cleanCart,
        delay
      );
  }


  /* ==========================================================
     HIDE DUPLICATE CART PROPERTY
     ========================================================== */

  function hideSideShieldProperty() {
    const escapedName =
      SIDE_SHIELD_PROPERTY
        .replace(
          /[.*+?^${}()|[\]\\]/g,
          '\\$&'
        );


    const propertyPattern =
      new RegExp(
        '^' +
        escapedName +
        '\\s*:\\s*.+$',
        'i'
      );


    document
      .querySelectorAll(
        'p, li, small, span, div, dt, dd'
      )
      .forEach((element) => {
        /*
         * Never touch EasyFlow product controls.
         */
        if (
          element.closest(
            '.ef__product-option-root, .ef__product-option'
          )
        ) {
          return;
        }


        /*
         * Don't hide interactive containers.
         */
        if (
          element.querySelector(
            'select, input, button'
          )
        ) {
          return;
        }


        const text =
          element.textContent
            ?.replace(
              /\s+/g,
              ' '
            )
            .trim() ||
          '';


        if (
          !propertyPattern.test(
            text
          )
        ) {
          return;
        }


        /*
         * Hide only the smallest matching
         * property element.
         */
        const childContainsProperty =
          Array.from(
            element.children
          ).some((child) => {
            const childText =
              child.textContent
                ?.replace(
                  /\s+/g,
                  ' '
                )
                .trim() ||
              '';


            return propertyPattern.test(
              childText
            );
          });


        if (
          childContainsProperty
        ) {
          return;
        }


        element.style.display =
          'none';


        element.setAttribute(
          'data-hwv-side-shield-property-hidden',
          'true'
        );
      });
  }


  /* ==========================================================
     WATCH CART AJAX
     ========================================================== */

  const originalFetch =
    window.fetch;


  window.fetch =
    async function(
      ...args
    ) {
      const response =
        await originalFetch.apply(
          this,
          args
        );


      try {
        const request =
          args[0];


        const url =
          typeof request ===
          'string'
            ? request
            : request?.url ||
              '';


        if (
          !cleanupRunning &&
          (
            url.includes(
              '/cart/change'
            ) ||
            url.includes(
              '/cart/update'
            ) ||
            url.includes(
              '/cart/add'
            ) ||
            url.includes(
              '/cart/clear'
            )
          )
        ) {
          scheduleCleanup(
            300
          );
        }

      } catch (error) {
        /*
         * Never interfere with Shopify fetch.
         */
      }


      return response;
    };


  /* ==========================================================
     WATCH CART DRAWER
     ========================================================== */

  const cartObserver =
    new MutationObserver(
      () => {
        hideSideShieldProperty();
      }
    );


  cartObserver.observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );


  /* ==========================================================
     INITIAL LOAD
     ========================================================== */

  function initializeCartCleanup() {
    hideSideShieldProperty();

    scheduleCleanup(
      500
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeCartCleanup
    );
  } else {
    initializeCartCleanup();
  }


  console.log(
    'HWV EasyFlow side shield cart cleanup loaded.'
  );
})();